import React from 'react';
import {CheckBoxField, NumberField, TextField} from "./index";
import {appendedOtherValue, createFakeEvent, generateOtherValue} from "./inputUtility";
import {partialOptionIndexInArray} from "../../Utilities/Types/arrayUtilities";
import {isTrimmedStringEmpty} from "../../Utilities/commonUtilities";
import {InputLayout} from "./InputLayout";
import Select from 'react-select';

import type {ListItem} from "../../Utilities/Types/listUtilities";
import type {ChangeHandler} from "../../types";

export interface SelectFieldAdapterProps {
    align?: string;
    defaultOption?: string;
    disabled?: boolean;
    error?: string;
    helpText?: string;
    includeDefaultOption?: boolean;
    includePlaceHolder?: boolean;
    inputClass?: string;
    isMulti?: boolean;
    isOtherTextNumberOnly?: boolean;
    isOtherTextPositiveNumberOnly?: boolean;
    isOtherTextWholeNumberOnly?: boolean;
    label: string;
    labelClass?: string;
    large?: string;
    large_columns?: string;
    listHasTextField?: string[];
    medium?: string;
    medium_columns?: string;
    name?: string;
    onChange?: ChangeHandler;
    options: ListItem[] | { value: number; text: string }[];
    otherTextClass?: string;
    otherTextLabel?: string;
    placeHolder?: string;
    showLabel?: boolean;
    showSelectAll?: boolean;
    small?: string;
    value?: string;
    wrapperClass?: string;
    defaultOptionValue?: string;
    setValue?: (value: string) => void;
    setError?: (error: string) => void;
}

export const SelectFieldAdapter: React.FC<SelectFieldAdapterProps> = ({
                                       align = "middle",
                                       defaultOption = "Select an Option",
                                       disabled = false,
                                       error,
                                       helpText,
                                       includeDefaultOption = false,
                                       includePlaceHolder = true,
                                       inputClass = "",
                                       isMulti = false,
                                       isOtherTextNumberOnly = false,
                                       isOtherTextPositiveNumberOnly = true,
                                       isOtherTextWholeNumberOnly = true,
                                       label,
                                       labelClass = "",
                                       large,
                                       large_columns,
                                       listHasTextField = [],
                                       medium,
                                       medium_columns,
                                       name = '',
                                       onChange = () => {},
                                       options,
                                       otherTextClass = ``,
                                       otherTextLabel = ``,
                                       placeHolder = "Select an Option",
                                       showLabel = true,
                                       showSelectAll = false,
                                       small,
                                       value,
                                       wrapperClass = ""
										   }: SelectFieldAdapterProps) => {

    const createSelectValue = (selectValue: string | number, selectLabel: string) => ({
        value: selectValue,
        label: selectLabel
    });

    const handleSelectFieldChange = (selectedOption: unknown) => {
        const result = createFakeEvent(isMulti ? String(convertValueArray(selectedOption as { value: string | number }[])) : String((selectedOption as { value: string | number }).value), name);
        onChange(result);
    };

    const convertValueArray = (selectedOptions: { value: string | number }[]) => {
        return selectedOptions.map((opt: { value: string | number }) => (opt.value));
    };

    let selectOptions = options.map((option: ListItem) => {
        return createSelectValue(option.value, option.text);
    });

    let defaultValue: { value: string | number; label: string } | null = null;
    if (includeDefaultOption) {
        defaultValue = createSelectValue("", defaultOption);
        selectOptions = [defaultValue, ...selectOptions];
    }

    const handleUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
        const otherValue = event.target.value;
        const newOtherValue = generateOtherValue(String(convertedValue ?? ''), otherValue);
        onChange(createFakeEvent(newOtherValue, name));
    };

    const otherSelectListValueIndex = partialOptionIndexInArray(listHasTextField, value ?? '');
    const hasTextField = otherSelectListValueIndex > -1;
    const selectListValue = hasTextField ? listHasTextField[otherSelectListValueIndex] : value;
    const otherTextValue = hasTextField ? appendedOtherValue(value ?? '', selectListValue ?? '') : "";

    type SelectOption = { value: string | number; label: string };
    let convertedValue: SelectOption | SelectOption[] | null = null;
    if (selectListValue) {
        convertedValue = isMulti
            ? String(selectListValue).split(',').map((opt: string) => (selectOptions.find((f: SelectOption) => String(f.value) === opt))).filter(Boolean) as SelectOption[]
            : selectOptions.find((f: SelectOption) => String(f.value) === selectListValue) ?? null;
    } else if (includeDefaultOption) {
        convertedValue = defaultValue;
    }

    const handleChangeToggleSelectAll = () => {
        let selectList: (string | number)[] = [];
        const convertedArray = Array.isArray(convertedValue) ? convertedValue : [];
        if (options.length !== convertedArray.length)
            selectList = options.map((option: ListItem) => option.value);

        const result = createFakeEvent(String(selectList), name);
        onChange(result);
    };

    const convertedArray = Array.isArray(convertedValue) ? convertedValue : [];
    const toggleSelectAllLabel = options.length === convertedArray.length
        ? "Deselect All"
        : "Select All";

    const toggleButtonChecked = options.length === convertedArray.length;

    return (
        <InputLayout
            align={align}
            disabled={disabled}
            error={error}
            helpText={helpText}
            inputClass={inputClass}
            label={label}
            labelClass={labelClass}
            large={large}
            large_columns={large_columns}
            medium={medium}
            medium_columns={medium_columns}
            name={name}
            showLabel={showLabel}
            small={small}
            wrapperClass={wrapperClass}
        >
            {
                showSelectAll &&
                <CheckBoxField
                    checked={toggleButtonChecked}
                    label={toggleSelectAllLabel}
                    name={`${name}_btnToggleSelectAll`}
                    onChange={handleChangeToggleSelectAll}
                    showLabel
                />
            }
            <Select
                className={`react-select`}
                classNamePrefix={`react-select`}
                inputId={name}
                isDisabled={disabled}
                isMulti={isMulti}
                name={name}
                onChange={handleSelectFieldChange}
                options={selectOptions}
                placeholder={includePlaceHolder ? placeHolder : ``}
                value={convertedValue}
                aria-label={name}
            />
            {
                hasTextField &&
                <>
                    {
                        isOtherTextNumberOnly &&
                        <NumberField
                            disabled={disabled}
                            isPositiveNumberOnly={isOtherTextPositiveNumberOnly}
                            isWholeNumberOnly={isOtherTextWholeNumberOnly}
                            label={otherTextLabel || `Specify Other Text`}
                            name={`${name}_text`}
                            onChange={handleUpdate}
                            showLabel={!isTrimmedStringEmpty(otherTextLabel)}
                            value={otherTextValue}
                        />
                    }
                    {
                        !isOtherTextNumberOnly &&
                        <TextField
                            disabled={disabled}
                            label={otherTextLabel || `Specify Other Text`}
                            name={`${name}_text`}
                            onChange={handleUpdate}
                            showLabel={!isTrimmedStringEmpty(otherTextLabel)}
                            value={otherTextValue}
                        />
                    }
                </>
            }
        </InputLayout>
    );
};
